import { useState, useEffect } from 'react';
import {
  Award,
  Server,
  Layers,
  LogOut,
  Flame,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { api } from './api/client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginView } from './components/LoginView';
import { BillingModal } from './components/BillingModal';
import { DashboardView } from './components/DashboardView';
import { QuizEngineView } from './components/QuizEngineView';
import { ExamReportView } from './components/ExamReportView';
import type {
  CertificationWithDetails,
  Question,
  QuestionClientView,
  ExamSessionMode,
  QuestionOptionKey,
  FinalizeExamResponse
} from '@shared/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  }
});

type AppView = 'dashboard' | 'quiz' | 'report';

function MainApp() {
  const { user, isLoading: authLoading, logout } = useAuth();
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [selectedCertId, setSelectedCertId] = useState<string>('asq-cpgp');

  // Exam session state
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeSessionMode, setActiveSessionMode] = useState<ExamSessionMode>('study');
  const [activeQuestions, setActiveQuestions] = useState<(Question | QuestionClientView)[]>([]);
  const [userAnswers, setUserAnswers] = useState<Record<string, QuestionOptionKey>>({});
  const [lastExamReport, setLastExamReport] = useState<FinalizeExamResponse | null>(null);

  // Health check query
  const { data: healthData } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.getHealth(),
    refetchInterval: 30000
  });

  // Spaced repetition due questions query (scoped to logged-in user)
  const { data: spacedData, refetch: refetchSpaced } = useQuery({
    queryKey: ['spaced-review', user?.id],
    queryFn: () => (user?.id ? api.getSpacedReview(user.id) : Promise.resolve({ total_due: 0, questions: [] })),
    enabled: !!user?.id,
    refetchInterval: 60000
  });

  const dueReviewCount = spacedData?.total_due || 0;

  // Stripe Billing & 14-Day Free Trial state and query
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const { data: billingData, refetch: refetchBilling } = useQuery({
    queryKey: ['billing-status', user?.id],
    queryFn: () => (user?.id ? api.getBillingStatus() : Promise.resolve(null)),
    enabled: !!user?.id,
    refetchInterval: 60000
  });

  // Certifications query with domain breakdowns
  const { data: certificationsData, isLoading: certsLoading } = useQuery({
    queryKey: ['certifications'],
    queryFn: () => api.getCertifications()
  });

  const certifications: CertificationWithDetails[] = certificationsData || [
    {
      id: 'asq-cpgp',
      title: 'ASQ CPGP',
      description: 'Certified Pharmaceutical GMP Professional — FDA 21 CFR 210/211, EU GMP Annex 1, Data Integrity',
      total_questions_count: 30,
      domains: [],
      metrics: { total_questions: 30, verified_questions: 30 }
    },
    {
      id: 'raps-rac-drugs',
      title: 'RAPS RAC Drugs',
      description: 'Regulatory Affairs Certification (Drugs) — FDA IND/NDA, EU CTD, ICH Quality Guidelines',
      total_questions_count: 3,
      domains: [],
      metrics: { total_questions: 3, verified_questions: 3 }
    }
  ];

  // Auto-select first cert if not set
  useEffect(() => {
    if (certifications.length > 0 && !selectedCertId) {
      setSelectedCertId(certifications[0].id);
    }
  }, [certifications, selectedCertId]);

  // Handler: Start Exam
  const handleStartExam = async (
    mode: ExamSessionMode,
    questionCount: number,
    domainFilter?: string[]
  ) => {
    if (billingData && !billingData.has_access) {
      setIsBillingModalOpen(true);
      return;
    }
    try {
      const res = await api.startExam({
        cert_id: selectedCertId,
        mode,
        question_count: questionCount,
        domains: domainFilter
      });

      setActiveSessionId(res.session.id);
      setActiveSessionMode(res.session.mode);
      setActiveQuestions(res.questions);
      setUserAnswers({});
      setCurrentView('quiz');
    } catch (err: any) {
      alert(`Could not start exam session: ${err.message}`);
    }
  };

  // Handler: Daily Challenge
  const handleStartDailyChallenge = async () => {
    try {
      const res = await api.getDailyChallenge();
      // Start a 1-question study session for daily challenge
      const sessionRes = await api.startExam({
        cert_id: res.question.cert_id || selectedCertId,
        mode: 'study',
        question_count: 1
      });

      // Override questions with the specific daily challenge question
      setActiveSessionId(sessionRes.session.id);
      setActiveSessionMode('study');
      setActiveQuestions([res.question]);
      setUserAnswers({});
      setCurrentView('quiz');
    } catch (err: any) {
      alert(`Could not load daily challenge: ${err.message}`);
    }
  };

  // Handler: Spaced Review Drill
  const handleStartSpacedReview = async () => {
    if (!user) return;
    if (billingData && !billingData.has_access) {
      setIsBillingModalOpen(true);
      return;
    }
    try {
      const dueRes = await api.getSpacedReview(user.id);
      if (!dueRes.questions || dueRes.questions.length === 0) {
        alert('All caught up! No questions currently due for SM-2 recall review.');
        return;
      }

      // Create a study session for the due review questions
      const sessionRes = await api.startExam({
        cert_id: selectedCertId,
        mode: 'study',
        question_count: dueRes.questions.length
      });

      setActiveSessionId(sessionRes.session.id);
      setActiveSessionMode('study');
      setActiveQuestions(dueRes.questions);
      setUserAnswers({});
      setCurrentView('quiz');
    } catch (err: any) {
      alert(`Could not start spaced review: ${err.message}`);
    }
  };

  // Handler: Test Daily Challenge Email Dispatch
  const handleSimulateCron = async () => {
    try {
      const res = await api.triggerDailyCron();
      alert(`[Daily Challenge Dispatched]\nDaily challenge email sent to ${res.sentCount} active subscriber(s).\nQuestion ID: ${res.questionId || 'N/A'}`);
    } catch (err: any) {
      alert(`Daily challenge dispatch failed: ${err.message}`);
    }
  };

  // Handler: Submit Answer in Quiz
  const handleSubmitAnswerApi = async (
    questionId: string,
    option: QuestionOptionKey,
    timeSpent: number
  ) => {
    if (!activeSessionId) {
      throw new Error('No active exam session');
    }
    setUserAnswers(prev => ({ ...prev, [questionId]: option }));
    const result = await api.submitAnswer(activeSessionId, {
      question_id: questionId,
      selected_option: option,
      time_spent_seconds: timeSpent
    });
    // Invalidate spaced review query to update due counts
    refetchSpaced();
    return result;
  };

  // Handler: Toggle Bookmark
  const handleToggleBookmarkApi = async (questionId: string) => {
    const res = await api.toggleBookmark(questionId);
    return res.bookmarked;
  };

  // Handler: Finish Exam Session
  const handleFinishExam = async () => {
    if (!activeSessionId) return;
    try {
      const report = await api.finalizeExam(activeSessionId);
      setLastExamReport(report);
      setCurrentView('report');
    } catch (err: any) {
      alert(`Failed to finalize exam: ${err.message}`);
    }
  };

  // If checking session state, display clean clinical loading screen
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center space-y-4 text-slate-600 dark:text-slate-300 font-sans transition-colors duration-200">
        <div className="w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-mono tracking-tight">Verifying Regulatory Profile...</p>
      </div>
    );
  }

  // Exclusive Login Gate: Sign in with Google
  if (!user) {
    return <LoginView />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans selection:bg-indigo-500/30 selection:text-indigo-900 dark:selection:text-indigo-200 transition-colors duration-200">
      {/* Top Clinical Header */}
      <header className="border-b border-slate-200 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/80 backdrop-blur sticky top-0 z-50 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center space-x-3 cursor-pointer group shrink-0"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 via-indigo-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition shrink-0">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div className="shrink-0">
              <div className="flex items-center space-x-2">
                <span className="font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap bg-gradient-to-r from-indigo-600 via-cyan-600 to-slate-900 dark:from-indigo-300 dark:via-cyan-300 dark:to-slate-100 bg-clip-text text-transparent">
                  BioPharm Exam Prep
                </span>
                <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-bold whitespace-nowrap hidden lg:inline-block">
                  LIVE BENCHMARK
                </span>
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-mono whitespace-nowrap hidden md:block">
                ASQ CPGP & RAPS RAC • FDA 21 CFR Benchmark
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3.5">
            {/* System status indicator */}
            <div className="hidden sm:flex items-center space-x-2 text-xs font-mono bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full px-3 py-1.5 shadow-sm">
              <Server className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
              <span className="text-slate-500 dark:text-slate-400">System:</span>
              <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                {healthData?.status || 'Online'}
              </span>
            </div>

            {currentView !== 'dashboard' && (
              <button
                onClick={() => setCurrentView('dashboard')}
                className="text-xs font-semibold text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700/80 rounded-xl px-3.5 py-1.5 transition flex items-center gap-1.5 cursor-pointer"
              >
                <Layers className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                <span>Dashboard</span>
              </button>
            )}

            {/* 14-Day Free Trial / Subscription Status Pill */}
            {billingData && (
              <button
                onClick={() => setIsBillingModalOpen(true)}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-medium transition cursor-pointer shadow-sm border ${
                  billingData.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/80'
                    : billingData.has_access
                    ? 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/80'
                    : 'bg-amber-500 hover:bg-amber-600 border-amber-600 text-white animate-pulse'
                }`}
                title="Manage 14-Day Free Trial & Subscription"
              >
                {billingData.status === 'active' ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span>Pro Active</span>
                  </>
                ) : billingData.has_access ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                    <span className="hidden sm:inline">14d Trial:</span>
                    <span className="font-bold">{billingData.trial_days_remaining}d left</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Subscribe (14d Free Trial)</span>
                  </>
                )}
              </button>
            )}

            {/* User Profile & Sign Out Pill */}
            <div className="flex items-center space-x-2 pl-2 sm:pl-3 border-l border-slate-200 dark:border-slate-800">
              <div className="flex items-center space-x-2">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name || 'User'}
                    className="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 object-cover shadow-sm"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 flex items-center justify-center text-xs font-bold border border-indigo-200 dark:border-indigo-800">
                    {(user.name || user.email || 'U').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="hidden lg:block text-left">
                  <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[130px]">
                    {user.name || user.email.split('@')[0]}
                  </div>
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 font-mono flex items-center gap-0.5 font-medium">
                    <Flame className="w-3 h-3 text-amber-500 shrink-0 fill-amber-500" />
                    <span>{user.streak_days}d streak</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => logout()}
                title="Sign Out"
                className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main App Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {certsLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4 text-slate-400">
            <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-mono">Loading Regulatory Question Bank...</p>
          </div>
        ) : (
          <>
            {currentView === 'dashboard' && (
              <DashboardView
                certifications={certifications}
                selectedCertId={selectedCertId}
                onSelectCertId={setSelectedCertId}
                onStartExam={handleStartExam}
                onStartDailyChallenge={handleStartDailyChallenge}
                dueReviewCount={dueReviewCount}
                onStartSpacedReview={handleStartSpacedReview}
                onSimulateCron={handleSimulateCron}
              />
            )}

            {currentView === 'quiz' && activeSessionId && (
              <QuizEngineView
                sessionId={activeSessionId}
                mode={activeSessionMode}
                questions={activeQuestions}
                onFinishExam={handleFinishExam}
                onExit={() => setCurrentView('dashboard')}
                onSubmitAnswerApi={handleSubmitAnswerApi}
                onToggleBookmarkApi={handleToggleBookmarkApi}
              />
            )}

            {currentView === 'report' && lastExamReport && (
              <ExamReportView
                report={lastExamReport}
                questions={activeQuestions}
                userAnswers={userAnswers}
                onRetake={() => handleStartExam(activeSessionMode, activeQuestions.length)}
                onReturnDashboard={() => setCurrentView('dashboard')}
              />
            )}
          </>
        )}
      </main>

      {/* Stripe Billing & 14-Day Free Trial Modal */}
      <BillingModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        billingStatus={billingData}
        onRefreshBilling={refetchBilling}
      />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MainApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}
